import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, XCircle, Loader2, KeyRound } from 'lucide-react';
import AuthService from '../../services/authService';
import AuthShell, { AuthNotice } from '../Auth/AuthShell';

const ActivateAccount = () => {
  const { uid, token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const validateToken = async () => {
      try {
        await AuthService.validateActivationToken(uid, token);
        setTokenValid(true);
      } catch (err) {
        console.error('Token validation error:', err);
        setError('Invalid or expired activation link. Please contact support.');
      } finally {
        setLoading(false);
      }
    };
    validateToken();
  }, [uid, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await AuthService.activateAccount(uid, token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      console.error('Account activation error:', err);
      // The activation endpoint returns { error: "..." } (password complexity,
      // expired token, already activated); fall back to message for anything else.
      const { error: apiError, message } = err.response?.data || {};
      setError(
        (typeof apiError === 'string' && apiError) ||
        message ||
        'An error occurred. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AuthShell>
        <div className="flex justify-center py-10" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading" />
        </div>
      </AuthShell>
    );
  }

  if (!tokenValid) {
    return (
      <AuthShell
        icon={<XCircle className="h-6 w-6" />}
        title="Invalid Activation Link"
        subtitle={error}
      />
    );
  }

  const eyeButton = (shown, toggle) => (
    <button
      type="button"
      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
      onClick={toggle}
      aria-label={shown ? 'Hide password' : 'Show password'}
    >
      {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
    </button>
  );

  return (
    <AuthShell
      icon={<KeyRound className="h-6 w-6" />}
      title="Activate Your Account"
      subtitle="Set your password to complete the activation process"
    >
      <form className="space-y-5" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="password" className="label">Password</label>
          <div className="relative">
            <input
              id="password"
              name="password"
              type={showPassword ? "text" : "password"}
              required
              autoComplete="new-password"
              className="input h-11 pr-11"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {eyeButton(showPassword, () => setShowPassword(!showPassword))}
          </div>
        </div>
        <div>
          <label htmlFor="confirm-password" className="label">Confirm Password</label>
          <div className="relative">
            <input
              id="confirm-password"
              name="confirm-password"
              type={showConfirmPassword ? "text" : "password"}
              required
              autoComplete="new-password"
              className="input h-11 pr-11"
              placeholder="Confirm Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
            {eyeButton(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword))}
          </div>
        </div>

        {error && <AuthNotice tone="danger">{error}</AuthNotice>}

        {success && (
          <AuthNotice tone="success">
            Account activated successfully! Redirecting to login...
          </AuthNotice>
        )}

        <button type="submit" disabled={loading} className="btn btn-primary btn-lg w-full">
          {loading ? 'Activating...' : 'Activate Account'}
        </button>
      </form>
    </AuthShell>
  );
};

export default ActivateAccount;